import React from 'react'
import { Button, ButtonProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Extinde ButtonProps cu variante custom
export interface StandardButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  loading?: boolean
}

/**
 * StandardButton - Buton unificat pentru toată aplicația
 * 
 * Variante:
 * - primary: Buton principal (albastru accent)
 * - secondary: Buton secundar (transparent cu border)  
 * - success: Acțiuni pozitive (verde)
 * - danger: Acțiuni de ștergere/pericol (roșu)
 * - warning: Acțiuni de atenție (galben)
 * - ghost: Minimal, fără background
 */
export const StandardButton: React.FC<StandardButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  disabled,
  loading,
  sx,
  ...props
}) => {
  const theme = useTheme()
  
  // Mapăm variantele noastre la variantele MUI și stiluri custom
  const getButtonStyles = () => {
    const { designSystem } = theme
    const colors = theme.palette.mode === 'dark' 
      ? designSystem.colors.dark 
      : designSystem.colors.light

    const baseStyles = {
      borderRadius: designSystem.components.button.borderRadius,
      fontWeight: designSystem.components.button.fontWeight,
      textTransform: designSystem.components.button.textTransform,
      fontSize: designSystem.typography.variants.button.fontSize,
      letterSpacing: designSystem.typography.variants.button.letterSpacing,
      boxShadow: 'none',
      transition: 'all 0.2s ease-in-out',
      
      // Size variants
      ...(size === 'small' && {
        height: designSystem.components.button.height.small,
        padding: `${designSystem.components.button.padding.vertical - 2}px ${designSystem.components.button.padding.horizontal - 4}px`,
        fontSize: '13px',
      }),
      ...(size === 'medium' && {
        height: designSystem.components.button.height.medium,
        padding: `${designSystem.components.button.padding.vertical}px ${designSystem.components.button.padding.horizontal}px`,
      }),
      ...(size === 'large' && {
        height: designSystem.components.button.height.large,
        padding: `${designSystem.components.button.padding.vertical + 4}px ${designSystem.components.button.padding.horizontal + 8}px`,
        fontSize: '16px',
      }),
    }

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: colors.accent.primary,
          color: '#ffffff',
          border: 'none',
          '&:hover': {
            backgroundColor: colors.accent.hover,
            boxShadow: designSystem.shadows.sm,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }

      case 'secondary':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: colors.text.primary,
          border: `1px solid ${colors.border.light}`,
          '&:hover': {
            backgroundColor: colors.background.elevated,
            borderColor: colors.accent.primary,
            boxShadow: designSystem.shadows.sm,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }

      case 'success':
        return {
          ...baseStyles,
          backgroundColor: designSystem.colors.status.success,
          color: '#ffffff',
          border: 'none',
          '&:hover': {
            backgroundColor: designSystem.colors.status.success,
            filter: 'brightness(0.9)',
            boxShadow: designSystem.shadows.sm,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }

      case 'danger':
        return {
          ...baseStyles,
          backgroundColor: designSystem.colors.status.error,
          color: '#ffffff',
          border: 'none',
          '&:hover': {
            backgroundColor: designSystem.colors.status.error,
            filter: 'brightness(0.9)',
            boxShadow: designSystem.shadows.sm,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }

      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: designSystem.colors.status.warning,
          color: theme.palette.mode === 'dark' ? '#000000' : '#000000', // Text negru pe galben
          border: 'none',
          '&:hover': {
            backgroundColor: designSystem.colors.status.warning,
            filter: 'brightness(0.9)',
            boxShadow: designSystem.shadows.sm,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }

      case 'ghost':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: colors.text.secondary,
          border: 'none',
          padding: `${designSystem.components.button.padding.vertical}px ${designSystem.components.button.padding.horizontal / 2}px`,
          '&:hover': {
            backgroundColor: colors.background.elevated,
            color: colors.text.primary,
            boxShadow: 'none',
          },
        }

      default:
        return baseStyles
    }
  }

  return (
    <Button
      disabled={disabled || loading}
      variant="contained" // Folosim mereu contained și customizăm stilurile
      sx={{
        ...getButtonStyles(),
        ...sx,
        ...(disabled && {
          opacity: 0.6,
          cursor: 'not-allowed',
          '&:hover': {
            transform: 'none',
          },
        }),
      }}
      {...props}
    >
      {loading ? 'Se încarcă...' : children}
    </Button>
  )
}

export default StandardButton
